import * as React from "react";
import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material";
import { Formik } from "formik";
import * as Yup from "yup";
import { AgeInfo } from "../components/AgeInfo";
import { CheckBoxLabel } from "../components/CheckboxLabel";
import { postNotification } from "../../utils/Notifications";
import { postData } from "../../utils/APIUtilities";

export async function signUp(
  userCredentials,
  updateFormState,
  setProcessing,
  updateFormType
) {
  const { password, email, firstName, lastName, policy, marketing } =
    userCredentials;
  const username = userCredentials?.username || userCredentials?.email;
  try {
    setProcessing(true);

    if (!email) throw new Error("missing email");
    if (!firstName) throw new Error("missing firstName");
    if (!lastName) throw new Error("missing lastName");
    if (!password) throw new Error("missing password");
    if (!policy) throw new Error("missing policy");

    updateFormState(userCredentials);

    let body = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      username: username.trim(),
      email: email.trim(),
      password: password,
      sendNotifications: marketing?.toLowerCase() === "yes",
    };

    await postData("/users", { body });

    postNotification("success", "sign up success!");
    updateFormType("verify");
  } catch (err) {
    postNotification("error", err);
  } finally {
    setProcessing(false);
  }
}

export default function CognitoSignUpForm({
  formState,
  updateFormState,
  setProcessing,
  updateFormType,
}) {
  const onSubmit = async (values) => {
    await signUp(values, updateFormState, setProcessing, updateFormType);
  };

  return (
    <Formik
      initialValues={{
        email: formState?.username || formState?.email || null,
        password: formState?.password || null,
        firstName: formState?.firstName || null,
        lastName: formState?.lastName || null,
        marketing: "",
        policy: false,
      }}
      enableReinitialize
      validationSchema={Yup.object().shape({
        email: Yup.string()
          .nullable()
          .max(128)
          .email("Must be a valid email")
          .required("Required"),
        password: Yup.string().nullable().max(255).required("Required"),
        firstName: Yup.string().nullable().max(128).required("Required"),
        lastName: Yup.string().nullable().max(128).required("Required"),
        marketing: Yup.string().nullable().required("Required"),
        policy: Yup.boolean().oneOf([true], "Required"),
      })}
      onSubmit={onSubmit.bind(this)}
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        setFieldValue,
        touched,
        values,
      }) => (
        <form noValidate onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                error={Boolean(touched.email && errors.email)}
                helperText={touched.email && errors.email}
                autoComplete="email"
                name="email"
                value={values.email}
                label="Email"
                variant="outlined"
                required
                fullWidth
                onBlur={handleBlur}
                onChange={(e) => {
                  const trimmedString = e.target.value.trim().toLowerCase();
                  setFieldValue("email", trimmedString);
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                error={Boolean(touched.firstName && errors.firstName)}
                helperText={touched.firstName && errors.firstName}
                autoComplete="firstName"
                name="firstName"
                value={values.firstName}
                label="First Name"
                variant="outlined"
                required
                fullWidth
                onBlur={handleBlur}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                error={Boolean(touched.lastName && errors.lastName)}
                helperText={touched.lastName && errors.lastName}
                autoComplete="lastName"
                name="lastName"
                value={values.lastName}
                label="Last Name"
                variant="outlined"
                required
                fullWidth
                onBlur={handleBlur}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                error={Boolean(touched.password && errors.password)}
                helperText={touched.password && errors.password}
                autoComplete="password"
                name="password"
                type="password"
                value={values.password}
                label="Password"
                variant="outlined"
                required
                fullWidth
                onBlur={handleBlur}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl required>
                <FormLabel>
                  Sometimes we send out exciting emails about upcoming events,
                  special offers, and deals from our partners. Would you like to
                  receive these emails?
                </FormLabel>

                <RadioGroup row>
                  <FormControlLabel
                    value={values.marketing}
                    control={
                      <Radio
                        name="marketing"
                        checked={values.marketing === "Yes"}
                        defaultChecked={false}
                        onClick={(e) => {
                          setFieldValue("marketing", "Yes");
                        }}
                      />
                    }
                    label="Yes"
                  />
                  <FormControlLabel
                    value={values.marketing}
                    control={
                      <Radio
                        name="marketing"
                        checked={values.marketing === "No"}
                        defaultChecked={false}
                        onClick={(e) => {
                          setFieldValue("marketing", "No");
                        }}
                      />
                    }
                    label="No"
                  />
                </RadioGroup>
              </FormControl>
              {Boolean(touched.marketing && errors.marketing) && (
                <FormHelperText error>{errors.marketing}</FormHelperText>
              )}
            </Grid>

            <AgeInfo />

            <Grid item xs={12}>
              <FormControlLabel
                sx={{
                  "& .MuiFormControlLabel-asterisk": {
                    color: "error.main",
                  },
                }}
                control={
                  <Checkbox
                    name="policy"
                    checked={values.policy}
                    onChange={setFieldValue.bind(
                      this,
                      "policy",
                      !values.policy
                    )}
                  />
                }
                label={
                  <CheckBoxLabel
                    text="I have read and agree with the Terms and Conditions"
                    required
                  />
                }
              />

              {Boolean(touched.policy && errors.policy) && (
                <FormHelperText error>{errors.policy}</FormHelperText>
              )}
            </Grid>

            <Grid item xs={12}>
              <Button type="submit" fullWidth variant="contained">
                Sign Up
              </Button>
            </Grid>
          </Grid>
        </form>
      )}
    </Formik>
  );
}
